import {
  BLACK_HOLE_HOLD_MS,
  FIST_STABLE_FRAMES,
  FORMATION_COOLDOWN_MS,
  GESTURE_ENTER_FRAMES,
  GESTURE_EXIT_FRAMES,
  SUPERNOVA_COOLDOWN_MS,
  SWIPE_MIN_DELTA,
  SWIPE_WINDOW_MS,
  TWO_HAND_FAR,
  TWO_HAND_NEAR,
} from "../config/constants.ts";
import { expansionFromHandSpan } from "../particles/visualBounds.ts";
import { clamp } from "../utils/math.ts";
import type { GestureId, HandFrame } from "../utils/types.ts";
import {
  extendedFingerCount,
  isClosedFist,
  isOpenPalm,
  isPinch,
  pinchDistanceNormalized,
  twoHandDistance,
} from "./classifiers.ts";
import { centroid as handCentroid } from "./landmarks.ts";

export interface GestureSample {
  hands: HandFrame[];
  timestamp: number;
}

export interface GestureOutput {
  id: GestureId;
  label: string;
  confidence: number;
  pinchAmount: number;
  expansionAmount: number;
  orbitX: number;
  orbitY: number;
  fireSupernova: boolean;
  fireSwipe: number;
  blackHoleHeld: boolean;
  noHands: boolean;
}

interface MachineState {
  candidate: GestureId;
  enterCount: number;
  active: GestureId;
  exitCount: number;
  fistFrames: number;
  fistArmed: boolean;
  lastSupernovaAt: number;
  lastSwipeAt: number;
  pinchActive: boolean;
  blackHoleStartedAt: number;
  swipeOriginX: number;
  swipeOriginT: number;
  lastOrbitX: number;
  lastOrbitY: number;
  pinchAmount: number;
}

export function createGestureState(): MachineState {
  return {
    candidate: "none",
    enterCount: 0,
    active: "none",
    exitCount: 0,
    fistFrames: 0,
    fistArmed: true,
    lastSupernovaAt: -SUPERNOVA_COOLDOWN_MS,
    lastSwipeAt: -FORMATION_COOLDOWN_MS,
    pinchActive: false,
    blackHoleStartedAt: 0,
    swipeOriginX: 0.5,
    swipeOriginT: 0,
    lastOrbitX: 0.5,
    lastOrbitY: 0.5,
    pinchAmount: 0.5,
  };
}

function emptyOutput(partial?: Partial<GestureOutput>): GestureOutput {
  return {
    id: "none",
    label: "NO HANDS DETECTED",
    confidence: 0,
    pinchAmount: 0.5,
    expansionAmount: 0.5,
    orbitX: 0.5,
    orbitY: 0.5,
    fireSupernova: false,
    fireSwipe: 0,
    blackHoleHeld: false,
    noHands: true,
    ...partial,
  };
}

function hysteresis(state: MachineState, next: GestureId): GestureId {
  if (next === state.active) {
    state.exitCount = 0;
    state.candidate = next;
    state.enterCount = GESTURE_ENTER_FRAMES;
    return state.active;
  }
  if (next === state.candidate) {
    state.enterCount += 1;
  } else {
    state.candidate = next;
    state.enterCount = 1;
  }
  if (state.active !== "none") {
    state.exitCount += 1;
    if (state.exitCount < GESTURE_EXIT_FRAMES) {
      return state.active;
    }
  }
  if (state.enterCount >= GESTURE_ENTER_FRAMES) {
    state.active = next;
    state.exitCount = 0;
  }
  return state.active;
}

/**
 * Classify a frame of smoothed hand landmarks. Important effects require
 * persistence across frames — a single noisy detection cannot fire supernova
 * or cycle formations.
 */
export function stepGestureMachine(
  state: MachineState,
  sample: GestureSample,
  sensitivity = 6,
): GestureOutput {
  const hands = sample.hands.filter((hand) => hand.landmarks.length >= 21 && hand.score > 0.4);
  if (hands.length === 0) {
    state.fistFrames = 0;
    state.pinchActive = false;
    state.blackHoleStartedAt = 0;
    state.active = "none";
    state.candidate = "none";
    state.enterCount = 0;
    return emptyOutput();
  }

  const sense = clamp(sensitivity / 10, 0.25, 1);
  const primary = hands[0]!;
  const pinchDist = pinchDistanceNormalized(primary.landmarks);
  const pinching = isPinch(pinchDist, state.pinchActive);
  state.pinchActive = pinching;
  const palm = isOpenPalm(primary.landmarks);
  const fist = isClosedFist(primary.landmarks);
  const center = handCentroid(primary.landmarks);

  let proposed: GestureId = "orbit";
  let expansionAmount = 0.5;
  let blackHoleHeld = false;
  let fireSwipe = 0;

  if (hands.length >= 2) {
    const span = twoHandDistance(hands[0]!.landmarks, hands[1]!.landmarks);
    if (span <= TWO_HAND_NEAR + (1 - sense) * 0.04) {
      if (state.blackHoleStartedAt === 0) state.blackHoleStartedAt = sample.timestamp;
      if (sample.timestamp - state.blackHoleStartedAt >= BLACK_HOLE_HOLD_MS) {
        proposed = "black-hole";
        blackHoleHeld = true;
      } else {
        proposed = "orbit";
      }
    } else {
      state.blackHoleStartedAt = 0;
      if (span >= TWO_HAND_FAR - (1 - sense) * 0.04) {
        proposed = "cosmic-expansion";
        expansionAmount = expansionFromHandSpan(span);
      }
    }
  } else {
    state.blackHoleStartedAt = 0;
  }

  if (proposed === "orbit") {
    if (fist) proposed = "supernova";
    else if (pinching) proposed = "gravity";
    else if (palm) proposed = "expand";
  }

  if (state.swipeOriginT === 0) {
    state.swipeOriginX = center.x;
    state.swipeOriginT = sample.timestamp;
  }
  const swipeDt = sample.timestamp - state.swipeOriginT;
  const swipeDx = center.x - state.swipeOriginX;
  if (swipeDt > SWIPE_WINDOW_MS) {
    state.swipeOriginX = center.x;
    state.swipeOriginT = sample.timestamp;
  } else if (
    Math.abs(swipeDx) >= SWIPE_MIN_DELTA * (1.15 - sense * 0.3) &&
    sample.timestamp - state.lastSwipeAt > FORMATION_COOLDOWN_MS &&
    extendedFingerCount(primary.landmarks) >= 2
  ) {
    fireSwipe = Math.sign(swipeDx) || 1;
    state.lastSwipeAt = sample.timestamp;
    proposed = "swipe";
    state.swipeOriginX = center.x;
    state.swipeOriginT = sample.timestamp;
  }

  // The black-hole hold already is the confirmation window — do not add extra
  // hysteresis after the gesture has been held long enough.
  if (blackHoleHeld) {
    state.active = "black-hole";
    state.candidate = "black-hole";
    state.enterCount = GESTURE_ENTER_FRAMES;
    state.exitCount = 0;
  }
  const active = blackHoleHeld ? "black-hole" : hysteresis(state, proposed);

  let fireSupernova = false;
  if (active === "supernova" && fist) {
    state.fistFrames += 1;
    if (
      state.fistArmed &&
      state.fistFrames >= FIST_STABLE_FRAMES &&
      sample.timestamp - state.lastSupernovaAt > SUPERNOVA_COOLDOWN_MS
    ) {
      fireSupernova = true;
      state.fistArmed = false;
      state.lastSupernovaAt = sample.timestamp;
    }
  } else {
    state.fistFrames = 0;
    if (!fist && (palm || pinchDist > 0.55)) {
      state.fistArmed = true;
    }
  }

  if (pinching) {
    state.pinchAmount = clamp(1 - pinchDist / 0.7, 0, 1);
  }

  state.lastOrbitX = center.x;
  state.lastOrbitY = center.y;

  const labels: Record<GestureId, string> = {
    none: "NO HANDS DETECTED",
    expand: "EXPAND",
    supernova: "SUPERNOVA",
    gravity: "GRAVITY",
    orbit: "ORBIT",
    "cosmic-expansion": "COSMIC EXPANSION",
    "black-hole": "BLACK HOLE",
    swipe: "CHANGE FORMATION",
  };

  return {
    id: fireSwipe !== 0 ? "swipe" : active,
    label: fireSwipe !== 0 ? labels.swipe : labels[active],
    confidence: primary.score,
    pinchAmount: state.pinchAmount,
    expansionAmount,
    orbitX: center.x,
    orbitY: center.y,
    fireSupernova,
    fireSwipe,
    blackHoleHeld,
    noHands: false,
  };
}

export { handCentroid };
